import React, { FunctionComponent, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  CloseIcon,
  List,
  Grid,
  ListItemSecondaryAction,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { useDebounce } from '@rbx/react-utilities';
import {
  searchClient as creatorSearchClient,
  DocSiteSearchResponse,
} from '@modules/clients/docSiteSearch';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { DocumentationContentType } from '@modules/clients/docSiteSearchType';
import throwError from '@modules/utilities/error';
import {
  TCategoryClearedInteraction,
  TCategoryClickedInteraction,
  trackSearchCategoryCleared,
  trackSearchCategoryClicked,
  trackSearchPerformed,
  trackSearchQueryCleared,
  trackSearchRecommendationClicked,
  trackSearchResultClicked,
  TResultClickedInteraction,
  isRealMouseClickEvent,
  TDialogClosedInteraction,
  isModifierKeyInteraction,
} from '@modules/search/searchEvents';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import { isInteractKey } from '@modules/layout/layout/utils/keyboardNavigationHandler';
import { isRecommendedSearchEnabled } from '@modules/clients/recommendedSearchClient';
import useNavigationSearch from '@modules/layout/topNavigation/hooks/useNavigationSearch';
import { TSearchResult } from './types/SearchResult';
import useSearchDialogStyles from './SearchDialogV2.styles';
import useSearchListItemStyles from './SearchListItem.styles';
import SearchListNullState from './SearchListNullState';
import AskAssistant from './AskAssistant';
import SearchContentV2 from './SearchContentV2';
import RecommendedSearch from './recommendations/RecommendedSearch';
import RecommendedSearchProvider from './recommendations/implementations/RecommendedSearchProvider';

import {
  DEFAULT_SEARCH_CATEGORY,
  SEARCH_CATEGORIES,
  SearchCategory,
} from './utils/searchCategories';
import SearchLoadingSkeleton from './SearchLoadingSkeleton';
import {
  processSearchResults,
  processCreatorSearchResponse,
  ProcessedSearchData,
} from './utils/searchDataProcessing';
import { SvgIconKeyboardReturn, SvgIconSearch } from './searchIcons';
import {
  SEARCH_INPUT_ID,
  SearchableContainer,
  ESearchNavigationElement,
  searchNavigationDomQueries,
  useSearchNavigation,
} from './hooks/useSearchNavigation';
import { FilterChipsSection } from './SearchFilterChips';
import { TSearchListItem } from './types/SearchListItem';
import { EndAdornment, placeholderEndAdornment } from './SearchListItem';

const SEARCH_DEBOUNCE_TIMER = 450;
// NOTE (@tchu, 2025-08-15): Add a delay to the loading timeout to avoid showing the loading state for a short time
const LOADING_TIMEOUT_DELAY = 300;
const NUM_RESULTS_TO_GET = 20;

// Module-level cache for search results
const searchCache: Record<string, TSearchResult[]> = {};

const getSavedResultsKey = (query: string, contentType: DocumentationContentType) => {
  return `${query}-${contentType}`;
};

interface SearchDialogV2Props {
  open: boolean;
  searchSessionId: string;
}

enum SearchContentType {
  Null = 'null',
  Loading = 'loading',
  Items = 'items',
  EmptyResults = 'empty',
  Nothing = 'nothing',
}

const RecommendationListItem = React.memo<{
  recommendation: string;
  onClickRecommendation: (recommendation: string) => void;
  listItemClasses: Record<string, string>;
  translate: (key: string) => string;
}>(({ recommendation, onClickRecommendation, listItemClasses, translate }) => {
  const itemRef = useRef<HTMLLIElement>(null);
  const { onKeyDownSearch } = useSearchNavigation(itemRef);
  const [endAdornment, setEndAdornment] = useState<EndAdornment>(placeholderEndAdornment);

  const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (isInteractKey(e)) {
      onClickRecommendation(recommendation);
    } else {
      onKeyDownSearch(e);
    }
  };

  const onFocus = () => {
    setEndAdornment({
      Icon: SvgIconKeyboardReturn,
    });
  };

  const onBlur = () => {
    setEndAdornment(placeholderEndAdornment);
  };

  return (
    <ListItem
      key={recommendation}
      ref={itemRef}
      className={listItemClasses.listItem}
      tabIndex={0}
      data-search-navigation-element={ESearchNavigationElement.ListItem}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={() => {
        onClickRecommendation(recommendation);
      }}>
      <ListItemIcon className={listItemClasses.listItemIcon} aria-hidden='true'>
        <SvgIconSearch />
      </ListItemIcon>
      <ListItemText
        className={listItemClasses.listItemText}
        primaryTypographyProps={{ color: 'inherit' }}
        primary={
          <Typography
            variant='body2'
            component='span'
            color='primary'
            aria-label={translate('Label.Title')}>
            {recommendation}
          </Typography>
        }
      />
      <ListItemSecondaryAction className={listItemClasses.listItemSecondaryAction}>
        <endAdornment.Icon fontSize='small' color='inherit' aria-hidden='true' />
      </ListItemSecondaryAction>
    </ListItem>
  );
});

RecommendationListItem.displayName = 'RecommendationListItem';

const SearchDialogV2: FunctionComponent<React.PropsWithChildren<SearchDialogV2Props>> = ({
  open,
  searchSessionId,
}) => {
  const { translate } = useTranslation();
  const { urlLocale: locale } = useCurrentLocale();
  const { classes: listItemClasses } = useSearchListItemStyles({});
  const { setSearchDialogOpenWithEvent } = useNavigationSearch();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<DocumentationContentType>(DEFAULT_SEARCH_CATEGORY);
  const [inputQuery, setInputQuery] = useState<string>('');
  const debouncedInputQuery = useDebounce<string>(inputQuery, SEARCH_DEBOUNCE_TIMER);
  const [instantQuery, setInstantQuery] = useState<string | null>(null);
  const [lastSearchedQuery, setLastSearchedQuery] = useState<string>('');
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [isRecommended, setIsRecommended] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<TSearchResult[]>([]);
  const [{ searchListItems, resultsByContentType, numberOfItems }, setProcessedData] =
    useState<ProcessedSearchData>(processSearchResults([], true));

  const searchListNullStateImpressionRef = useRef<string | null>(null);
  const recommendationImpressionRef = useRef<string | null>(null);
  const askAssistantImpressionRef = useRef<string | null>(null);

  const query = (instantQuery ?? debouncedInputQuery).trim();

  const isFilterDefault = filter === DEFAULT_SEARCH_CATEGORY;
  const isInputQueryEmpty = inputQuery.length === 0;

  let searchContentType: SearchContentType;

  if (isInputQueryEmpty || query.length === 0) {
    if (isFilterDefault) {
      searchContentType = SearchContentType.Null;
    } else {
      searchContentType = SearchContentType.Nothing;
    }
  } else if (isSearchLoading || (numberOfItems === 0 && lastSearchedQuery === '')) {
    searchContentType = SearchContentType.Loading;
  } else if (numberOfItems > 0) {
    searchContentType = SearchContentType.Items;
  } else {
    searchContentType = SearchContentType.EmptyResults;
  }

  const enableRecommendedSearch =
    isFilterDefault &&
    searchContentType !== SearchContentType.Loading &&
    isRecommendedSearchEnabled();

  const enableAskAssistant =
    isFilterDefault &&
    searchContentType !== SearchContentType.Loading &&
    numberOfItems > 0 &&
    process.env.enableSearchAskAssistant;

  const enableFilterChips =
    isFilterDefault &&
    [SearchContentType.Null, SearchContentType.Items].includes(searchContentType);

  const { classes } = useSearchDialogStyles({
    isSearchContentNothing: searchContentType === SearchContentType.Nothing,
  });

  const searchResultsFromCategorized = useMemo(() => {
    return Object.values(resultsByContentType).flat();
  }, [resultsByContentType]);

  useEffect(() => {
    if (inputQuery.length === 0) {
      React.startTransition(() => {
        setProcessedData(processSearchResults([], true));
        setSearchResults([]);
        setLastSearchedQuery('');
        setIsSearchLoading(false);
      });
    }
  }, [inputQuery]);

  // Search effect
  useEffect(() => {
    setIsError(false);

    let invalidated = false;
    let timeoutId: NodeJS.Timeout;

    const searchDocuments = async () => {
      if (query.length === 0) {
        React.startTransition(() => {
          setProcessedData(processSearchResults([], true));
          setSearchResults([]);
          setLastSearchedQuery('');
          setIsSearchLoading(false);
        });
        return;
      }

      const savedResultsKey = getSavedResultsKey(query, filter);
      if (savedResultsKey in searchCache) {
        const results = searchCache[savedResultsKey] ?? [];
        React.startTransition(() => {
          setProcessedData(processSearchResults(results, isFilterDefault));
          setSearchResults(results);
          setLastSearchedQuery(query);
          setIsSearchLoading(false);
        });
        return;
      }

      timeoutId = setTimeout(() => {
        setIsSearchLoading(true);
      }, LOADING_TIMEOUT_DELAY);

      let creatorSearchResponse: DocSiteSearchResponse;
      let processedResponse: TSearchResult[];
      let processedData: ProcessedSearchData;

      try {
        creatorSearchResponse = await creatorSearchClient.search({
          locale,
          keyword: query,
          pageSize: NUM_RESULTS_TO_GET,
          documentationContentType: filter.toString(),
          searchSessionId,
        });

        if (invalidated) return;

        processedResponse = processCreatorSearchResponse(creatorSearchResponse, translate);
        processedData = processSearchResults(processedResponse, isFilterDefault);
        trackSearchPerformed({
          category: filter,
          interaction: ESearchInteraction.Input,
          isRecommended,
          isSemantic: !!creatorSearchResponse.isSemantic,
          locale,
          query,
          searchResults: Object.values(processedData.resultsByContentType).flat(),
          searchSessionId,
        });
      } catch (e) {
        throwError(`SearchDialogV2: failed to fetch search results ${e}`);
        setIsError(true);
        clearTimeout(timeoutId);
        React.startTransition(() => {
          setProcessedData(processSearchResults([], true));
          setSearchResults([]);
          setLastSearchedQuery(query);
          setIsSearchLoading(false);
        });
        return;
      }

      clearTimeout(timeoutId);
      React.startTransition(() => {
        setIsError(creatorSearchResponse.isError);
        setProcessedData(processedData);
        setSearchResults(processedResponse);
        setLastSearchedQuery(query);
        setIsSearchLoading(false);
      });

      // we shouldn't save error results
      if (!creatorSearchResponse.isError) {
        searchCache[savedResultsKey] = processedResponse;
      }
    };

    searchDocuments();

    return () => {
      invalidated = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filter]);

  useEffect(() => {
    if (instantQuery) {
      setInstantQuery(null);
    }
  }, [instantQuery]);

  const currentFilter: SearchCategory | undefined = SEARCH_CATEGORIES.find(
    (f) => f.value === filter,
  );

  const resetSearchDialog = useCallback((): void => {
    setInputQuery('');
    setFilter(DEFAULT_SEARCH_CATEGORY);
    searchInputRef.current?.focus();
  }, [setInputQuery]);

  const onClose = useCallback(
    (interaction: TDialogClosedInteraction): void => {
      resetSearchDialog();
      setIsRecommended(false);
      setSearchDialogOpenWithEvent({ searchDialogOpen: false, interaction });
    },
    [resetSearchDialog, setSearchDialogOpenWithEvent],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (isInputQueryEmpty && event.key === 'Backspace') {
        resetSearchDialog();
        trackSearchCategoryCleared({
          category: filter,
          interaction: ESearchInteraction.KeyboardBackspace,
          isRecommended,
          locale,
          query: inputQuery,
          searchResults,
          searchSessionId,
        });
      } else if (event.key === 'ArrowDown') {
        // Navigate to the first search result item
        event.preventDefault();
        const firstSearchResult = searchNavigationDomQueries.getListItems()[0];
        if (firstSearchResult) {
          firstSearchResult.focus();
        }
      }
    },
    [
      filter,
      inputQuery,
      isInputQueryEmpty,
      isRecommended,
      locale,
      resetSearchDialog,
      searchResults,
      searchSessionId,
    ],
  );

  const onClickFilter = useCallback(
    (newFilter: SearchCategory, interaction: TCategoryClickedInteraction): void => {
      searchInputRef.current?.focus();
      if (newFilter.value === filter) {
        return;
      }
      setFilter(newFilter.value);
      trackSearchCategoryClicked({
        category: newFilter.value,
        interaction,
        isRecommended,
        locale,
        query: inputQuery,
        searchResults,
        searchSessionId,
      });
    },
    [filter, isRecommended, locale, inputQuery, searchResults, searchSessionId],
  );

  const onClearFilter = useCallback(
    (newFilter: SearchCategory, interaction: TCategoryClearedInteraction): void => {
      searchInputRef.current?.focus();
      if (newFilter.value === DEFAULT_SEARCH_CATEGORY) {
        return;
      }
      setFilter(DEFAULT_SEARCH_CATEGORY);
      trackSearchCategoryCleared({
        category: newFilter.value,
        interaction,
        isRecommended,
        locale,
        query: inputQuery,
        searchResults,
        searchSessionId,
      });
    },
    [isRecommended, locale, inputQuery, searchResults, searchSessionId],
  );

  const onSearchInput = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setInputQuery(event.target.value);
    setIsRecommended(false);
  }, []);

  const onClickItem = useCallback(
    (item: TSearchListItem, interaction: TResultClickedInteraction): void => {
      if (isModifierKeyInteraction(interaction)) {
        return;
      }
      onClose(ESearchInteraction.SearchCompleted);
    },
    [onClose],
  );

  const onClickContentItem = (item: TSearchListItem, interaction: TResultClickedInteraction) => {
    trackSearchResultClicked({
      category: filter,
      interaction,
      isRecommended,
      locale,
      query: inputQuery,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      searchResult: item.resultRef!,
      searchResults: searchResultsFromCategorized,
      searchSessionId,
    });
    if (isModifierKeyInteraction(interaction)) {
      return;
    }
    onClose(ESearchInteraction.SearchCompleted);
  };

  const onClickClearSearch = () => {
    trackSearchQueryCleared({
      category: filter,
      interaction: ESearchInteraction.Click,
      isRecommended,
      locale,
      query: inputQuery,
      searchResults,
      searchSessionId,
    });
    resetSearchDialog();
  };

  const onClickRecommendation = (recommendation: string) => {
    setIsRecommended(true);
    setInstantQuery(recommendation);
    setInputQuery(recommendation);
    trackSearchRecommendationClicked({
      interaction: ESearchInteraction.Click,
      locale,
      query: inputQuery,
      recommendation,
      searchSessionId,
    });
  };

  const onKeyDownTopFilterChip = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isInteractKey(event)) {
      onClearFilter(currentFilter as SearchCategory, ESearchInteraction.KeyboardEnterCategoryPill);
    }
  };
  const onClickTopFilterChip = (event: React.MouseEvent<HTMLDivElement>) => {
    // NOTE (@tchu, 2025-08-28): keyboard enter is handled in `onKeyDownTopFilterChip`
    if (isRealMouseClickEvent(event)) {
      onClearFilter(currentFilter as SearchCategory, ESearchInteraction.ClickCategoryPill);
    }
  };
  // eslint-disable-next-line react/no-unstable-nested-components
  const TopFilterChip = () => {
    return (
      <Chip
        label={translate(`${currentFilter?.translationKey}`) || currentFilter?.fallbackLabel}
        className={classes.searchFilterChip}
        size='small'
        color='secondary'
        tabIndex={0}
        onClick={onClickTopFilterChip}
        onKeyDown={onKeyDownTopFilterChip}
        deleteIcon={<CloseIcon color='secondary' fontSize='small' />}
        // NOTE (@tchu, 2025-08-28): make the entire chip clickable instead of the delete icon
        // onDelete is not accessible by keyboard enter, so we only handle it in `onClick`
        onDelete={onClickTopFilterChip}
      />
    );
  };

  return (
    <Dialog
      aria-label={translate('Label.Search')}
      open={open}
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown') {
          onClose(ESearchInteraction.KeyboardEscape);
          return;
        }
        // currently only two reasons, 'escapeKeyDown' and 'backdropClick'
        onClose(ESearchInteraction.ModalClickOut);
      }}
      maxWidth='XXLarge'
      classes={{
        paper: classes.dialogPaper,
      }}>
      <RecommendedSearchProvider>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.searchContainer} role='search'>
            <TextField
              label={null}
              fullWidth
              autoComplete='off'
              autoFocus
              id={SEARCH_INPUT_ID}
              placeholder={translate('Label.Search') || 'Search'}
              value={inputQuery}
              onInput={onSearchInput}
              onKeyDown={onKeyDown}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SvgIconSearch />
                    {!isFilterDefault && <TopFilterChip />}
                  </InputAdornment>
                ),
                endAdornment: (!isFilterDefault || !isInputQueryEmpty) && (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label={translate('Label.ClearSearch') || 'Clear Search'}
                      size='small'
                      onClick={onClickClearSearch}>
                      <CloseIcon color='secondary' fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
                inputProps: {
                  className: classes.searchInput,
                  'aria-label': translate('Label.Search'),
                },
                classes: {
                  root: classes.searchInputOutlineRoot,
                  notchedOutline: classes.searchInputOutline,
                },
              }}
            />
            <Typography variant='body2' className='sr-only'>
              {translate('Description.SearchScreenReaderHelpText')}
            </Typography>
            {searchContentType !== SearchContentType.Nothing && (
              <Divider className={classes.searchDivider} role='separator' />
            )}
          </div>

          {searchContentType === SearchContentType.Null && (
            <SearchListNullState
              impressionRef={searchListNullStateImpressionRef}
              onClickItem={onClickItem}
              locale={locale}
              searchSessionId={searchSessionId}
            />
          )}

          {searchContentType === SearchContentType.Loading && <SearchLoadingSkeleton />}

          {searchContentType === SearchContentType.EmptyResults && (
            <Grid item>
              <div className={classes.emptyResults}>
                {isError
                  ? translate('Label.ResultsErrorTryAgain')
                  : translate('Label.NoResultsFoundTryAgain')}
              </div>
            </Grid>
          )}

          <Grid
            item
            container
            direction='column'
            wrap='nowrap'
            data-list-container={SearchableContainer.List}
            className={classes.resultsContainer}>
            {searchContentType === SearchContentType.Items && (
              <SearchContentV2
                searchListItems={searchListItems}
                isFilterDefault={isFilterDefault}
                onClickItem={onClickContentItem}
                onClickFilter={onClickFilter}
                onClearFilter={onClearFilter}
              />
            )}

            {enableAskAssistant && (
              <AskAssistant
                impressionRef={askAssistantImpressionRef}
                locale={locale}
                searchQuery={inputQuery}
                searchSessionId={searchSessionId}
                searchResults={searchResultsFromCategorized}
              />
            )}

            {enableRecommendedSearch && (
              <React.Fragment>
                <Divider className={classes.searchDivider} role='separator' />
                <List
                  className={classes.bottomSearchList}
                  data-list-container={SearchableContainer.List}
                  tabIndex={-1}>
                  <RecommendedSearch
                    locale={locale}
                    maxRecommendations={1}
                    impressionRef={recommendationImpressionRef}
                    searchQuery={inputQuery}
                    searchSessionId={searchSessionId}
                    renderRecommendations={({ recommendations }) => (
                      <React.Fragment>
                        {recommendations.map((recommendation) => (
                          <RecommendationListItem
                            key={recommendation}
                            recommendation={recommendation}
                            onClickRecommendation={onClickRecommendation}
                            listItemClasses={listItemClasses}
                            translate={translate}
                          />
                        ))}
                      </React.Fragment>
                    )}
                  />
                </List>
              </React.Fragment>
            )}

            {enableFilterChips && (
              <React.Fragment>
                {!enableRecommendedSearch && (
                  <Divider className={classes.searchDivider} role='separator' />
                )}
                <FilterChipsSection onClickFilter={onClickFilter} />
              </React.Fragment>
            )}
          </Grid>
        </DialogContent>
      </RecommendedSearchProvider>
    </Dialog>
  );
};

export default SearchDialogV2;
